import { CommonModule, isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, PLATFORM_ID, ViewChild, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { take } from 'rxjs';
import * as THREE from 'three';

import { resolveReturnUrl } from '../auth/auth.flow';
import { AuthService } from '../auth/auth.service';

interface LoginForm {
  email: FormControl<string>;
  password: FormControl<string>;
}

@Component({
  selector: 'us-login-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  template: `
    <section class="login-page">
      <div class="login-page__panel login-page__panel--form">
        <div class="login-page__content">
          <header class="login-page__brand" aria-label="Ultra móviles">
            <img class="login-page__brand-logo" [src]="logoUrl" alt="Ultra móviles" />
          </header>

          <form class="login-page__form" [formGroup]="loginForm" (ngSubmit)="submit()" novalidate>
            <div class="login-page__field">
              <label class="login-page__visually-hidden" for="login-email">Usuario</label>
              <input
                id="login-email"
                formControlName="email"
                type="email"
                autocomplete="email"
                placeholder="Usuario"
              />
              <small class="login-page__hint" *ngIf="loginForm.controls.email.touched && loginForm.controls.email.invalid">
                Ingresá un email válido.
              </small>
            </div>

            <div class="login-page__field">
              <label class="login-page__visually-hidden" for="login-password">Contraseña</label>
              <input
                id="login-password"
                formControlName="password"
                type="password"
                autocomplete="current-password"
                placeholder="Contraseña"
              />
              <small
                class="login-page__hint"
                *ngIf="loginForm.controls.password.touched && loginForm.controls.password.invalid"
              >
                La contraseña es obligatoria.
              </small>
            </div>

            <p class="login-page__error" *ngIf="errorMessage() as errorMessage" role="alert">{{ errorMessage }}</p>

            <button class="login-page__submit" type="submit" [disabled]="submitting()">
              {{ submitting() ? 'Ingresando…' : 'Login' }}
            </button>
          </form>
        </div>
      </div>

      <div class="login-page__panel login-page__panel--visual" aria-hidden="true">
        <div #visualPanel class="login-page__vanta-surface"></div>
        <div class="login-page__visual-overlay"></div>
        <div class="login-page__visual-glow"></div>
      </div>
    </section>
  `,
  styles: [
    `
      .login-page {
        min-height: 100vh;
        display: grid;
        grid-template-columns: minmax(320px, 38%) 1fr;
        background: #ffffff;
      }

      .login-page__panel {
        min-height: 100vh;
      }

      .login-page__panel--form {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 48px 32px;
        background: #fffdfd;
      }

      .login-page__content {
        width: min(100%, 300px);
      }

      .login-page__brand {
        display: inline-flex;
        margin-bottom: 40px;
      }

      .login-page__brand-logo {
        width: 118px;
        height: auto;
        display: block;
      }

      .login-page__form {
        display: grid;
        gap: 12px;
      }

      .login-page__field {
        display: grid;
        gap: 6px;
      }

      .login-page__field input {
        width: 100%;
        padding: 10px 12px;
        border: 1px solid #bf2d31;
        border-radius: 3px;
        font: inherit;
        font-size: 0.95rem;
        color: #2f1217;
        background: #ffffff;
        box-sizing: border-box;
      }

      .login-page__field input::placeholder {
        color: #a79b9b;
      }

      .login-page__field input:focus {
        outline: 2px solid rgba(203, 31, 38, 0.2);
        outline-offset: 1px;
      }

      .login-page__hint,
      .login-page__error {
        margin: 0;
        font-size: 0.8rem;
      }

      .login-page__hint {
        color: #7f4a4d;
      }

      .login-page__error {
        color: #b01218;
      }

      .login-page__submit {
        margin-top: 2px;
        border: 0;
        border-radius: 3px;
        padding: 10px 16px;
        background: linear-gradient(180deg, #d6252e 0%, #bc171f 100%);
        color: #ffffff;
        font: inherit;
        font-weight: 500;
        cursor: pointer;
        box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.18);
      }

      .login-page__submit:disabled {
        cursor: wait;
        opacity: 0.7;
      }

      .login-page__actions {
        display: flex;
        justify-content: center;
        gap: 4px;
        flex-wrap: wrap;
        margin-top: 2px;
      }

      .login-page__text-action {
        padding: 0;
        border: 0;
        background: transparent;
        color: #5b3b3f;
        font: inherit;
        font-size: 0.78rem;
        text-decoration: underline;
        cursor: pointer;
      }

      .login-page__panel--visual {
        position: relative;
        overflow: hidden;
        isolation: isolate;
        background:
          radial-gradient(circle at top, rgba(255, 255, 255, 0.18), transparent 45%),
          linear-gradient(180deg, #cf1f28 0%, #8d141b 100%);
      }

      .login-page__vanta-surface,
      .login-page__visual-overlay,
      .login-page__visual-glow {
        position: absolute;
        inset: 0;
      }

      .login-page__vanta-surface {
        z-index: 0;
      }

      .login-page__visual-overlay {
        z-index: 1;
        background:
          radial-gradient(circle at 18% 18%, rgba(255, 255, 255, 0.16), transparent 28%),
          linear-gradient(160deg, rgba(214, 37, 46, 0.18) 0%, rgba(65, 8, 13, 0.58) 100%);
      }

      .login-page__visual-glow {
        z-index: 2;
        pointer-events: none;
        background:
          radial-gradient(circle at 72% 22%, rgba(255, 255, 255, 0.12), transparent 18%),
          radial-gradient(circle at 50% 50%, rgba(214, 37, 46, 0.1), transparent 34%),
          radial-gradient(circle at 50% 100%, rgba(0, 0, 0, 0.18), transparent 40%);
      }

      .login-page__visually-hidden {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }

      @media (max-width: 900px) {
        .login-page {
          grid-template-columns: 1fr;
        }

        .login-page__panel--visual {
          min-height: 240px;
        }

      }
    `
  ]
})
export class LoginPageComponent implements AfterViewInit, OnDestroy {
  readonly logoUrl = '/public/images/logo_principal_rojo.svg';
  readonly loginForm = new FormGroup<LoginForm>({
    email: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.email] }),
    password: new FormControl('', { nonNullable: true, validators: [Validators.required] })
  });

  readonly submitting = signal(false);
  readonly errorMessage = signal<string | null>(null);
  @ViewChild('visualPanel') private visualPanelRef?: ElementRef<HTMLElement>;

  private readonly platformId = inject(PLATFORM_ID);
  private readonly returnUrl: string;
  private isDestroyed = false;
  private vantaEffect?: { destroy(): void };
  private readonly vantaThree = { ...THREE, VertexColors: true } as typeof THREE & { VertexColors: boolean };

  private get browserWindow(): (Window & { THREE?: typeof THREE }) | null {
    return isPlatformBrowser(this.platformId) ? (window as Window & { THREE?: typeof THREE }) : null;
  }

  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
    private readonly route: ActivatedRoute
  ) {
    this.returnUrl = resolveReturnUrl(this.route.snapshot.queryParamMap.get('returnUrl'));
  }

  ngAfterViewInit(): void {
    void this.initializeVantaBackground();
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.vantaEffect?.destroy();
  }

  private async initializeVantaBackground(): Promise<void> {
    const browserWindow = this.browserWindow;

    if (!browserWindow || !this.visualPanelRef?.nativeElement) {
      return;
    }

    if (browserWindow.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      return;
    }

    try {
      browserWindow.THREE = this.vantaThree;
      const { default: NET } = await import('vanta/src/vanta.net.js');

      if (this.isDestroyed || !this.visualPanelRef?.nativeElement) {
        return;
      }

      this.vantaEffect = NET({
        el: this.visualPanelRef.nativeElement,
        THREE: this.vantaThree,
        backgroundAlpha: 0,
        color: 0xcf1f28,
        backgroundColor: 0xd9d9d9,
        gyroControls: false,
        maxDistance: 20,
        minHeight: 200,
        minWidth: 200,
        mouseControls: false,
        points: 10,
        scale: 1,
        scaleMobile: 1,
        showDots: false,
        spacing: 15,
        touchControls: false
      });
    } catch (error) {
      console.error('Failed to initialize Vanta NET background', error);
    }
  }

  submit(): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.submitting.set(true);
    this.errorMessage.set(null);

    this.authService
      .signInEmail(this.loginForm.getRawValue())
      .pipe(take(1))
      .subscribe((result) => {
        this.submitting.set(false);

        if (!result.success) {
          this.errorMessage.set(result.message);
          return;
        }

        void this.router.navigateByUrl(this.returnUrl);
      });
  }
}
